import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query'

import {
  approveCustomerKyc,
  changeCustomerStatus,
  createCustomer,
  rejectCustomerKyc,
  submitCustomerForReview,
  updateCustomer,
  uploadCustomerMedia,
  verifyCustomer,
} from '@/api/customers-api'
import type {
  CustomerMediaType,
  CustomerStatusChangeRequest,
  CustomerWriteRequest,
  VerifyCustomerRequest,
} from '@/api/types'

import { customerKeys } from './customer-keys'

/**
 * Commands over the customer module.
 *
 * Every one of these changes something a list row displays — a status chip, a KYC chip, the
 * arrears marker — so each invalidates the lists as well as the record it acted on. Exposure is
 * invalidated alongside, because blacklisting a customer changes the banner that stops another
 * branch lending to them.
 */
function invalidateCustomer(queryClient: QueryClient, customerId: string) {
  void queryClient.invalidateQueries({ queryKey: customerKeys.detail(customerId) })
  void queryClient.invalidateQueries({ queryKey: customerKeys.exposure(customerId) })
  void queryClient.invalidateQueries({ queryKey: customerKeys.lists() })
}

export function useCreateCustomer() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: CustomerWriteRequest) => createCustomer(request),
    onSuccess: (customer) => {
      // The create response is the complete record, so the detail screen the operator lands on
      // next renders immediately instead of asking for what has just been returned.
      queryClient.setQueryData(customerKeys.detail(customer.id), customer)
      void queryClient.invalidateQueries({ queryKey: customerKeys.lists() })
    },
  })
}

export function useUpdateCustomer(customerId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: CustomerWriteRequest) => updateCustomer(customerId, request),
    onSuccess: () => {
      invalidateCustomer(queryClient, customerId)
    },
  })
}

export function useSubmitCustomerForReview(customerId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => submitCustomerForReview(customerId),
    onSuccess: () => {
      invalidateCustomer(queryClient, customerId)
    },
  })
}

export function useApproveCustomerKyc(customerId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => approveCustomerKyc(customerId),
    onSuccess: () => {
      invalidateCustomer(queryClient, customerId)
    },
  })
}

export function useRejectCustomerKyc(customerId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (reason: string) => rejectCustomerKyc(customerId, { reason }),
    onSuccess: () => {
      invalidateCustomer(queryClient, customerId)
    },
  })
}

export function useChangeCustomerStatus(customerId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: CustomerStatusChangeRequest) => changeCustomerStatus(customerId, request),
    onSuccess: () => {
      invalidateCustomer(queryClient, customerId)
    },
  })
}

/**
 * Captures a specimen. Invalidates the customer record — its `media` array is where the new
 * capture's metadata shows up — and the specimen's own blob cache, so the panel that just uploaded
 * it shows the new image rather than the one cached from before.
 */
export function useUploadCustomerMedia(customerId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ mediaType, file }: { mediaType: CustomerMediaType; file: Blob }) =>
      uploadCustomerMedia(customerId, mediaType, file),
    onSuccess: (_media, { mediaType }) => {
      invalidateCustomer(queryClient, customerId)
      void queryClient.invalidateQueries({ queryKey: customerKeys.mediaBlob(customerId, mediaType) })
    },
  })
}

/**
 * Records that a teller confirmed the customer at the counter. Not the customer's own record, so
 * nothing about the customer query is invalidated — the result is a short-lived token the caller
 * holds itself (see `features/customers/verification-store.ts`), not a fact about the file.
 */
export function useVerifyCustomer(customerId: string) {
  return useMutation({
    mutationFn: (request: VerifyCustomerRequest) => verifyCustomer(customerId, request),
  })
}
